/**
 * ExperiencePage — "The Experience": the interactive pre-purchase showcase reachable from the
 * landing-page nav. There is no free trial, so this page carries the conversion load: seven short
 * chapters, each with a LIVE ANIMATED mock of the real product. Chapter 1 is interactive — the
 * visitor picks a living arrangement and the mock fact-find + results re-curate to it (owner
 * feedback 10 Jul: don't let the tour read as aged-care-only). All visuals are built in-code —
 * no video assets required; a produced walkthrough video can become an extra chapter later.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles, ArrowLeft, ArrowRight, Play, User, CheckCircle2, Lightbulb,
  TrendingUp, Trophy, FileText, Bot, Briefcase, ChevronRight, RotateCcw, SlidersHorizontal,
} from 'lucide-react';

/* ---------- animation utilities ---------- */

/** Count from 0 to target over `duration` ms, restarting whenever `replayKey` changes. */
function useCountUp(target: number, duration = 1400, replayKey: string | number = 0, delay = 0): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf: number;
    let start: number | undefined;
    setValue(0);
    const tick = (now: number) => {
      if (start === undefined) start = now + delay;
      const elapsed = now - start;
      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, replayKey, delay]);
  return value;
}

const aud = (n: number): string => '$' + n.toLocaleString('en-AU');

interface StaggerProps {
  replayKey: string | number;
  step?: number;
  children: React.ReactNode;
}

/** Reveal children one-by-one with a stagger, restarting on replayKey change. */
const Stagger = ({ replayKey, step = 450, children }: StaggerProps) => {
  const items = React.Children.toArray(children);
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    const timers = items.map((_, i) => setTimeout(() => setShown((s) => Math.max(s, i + 1)), 300 + i * step));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayKey, step, items.length]);
  return items.map((child, i) => (
    <div
      key={i}
      className="transition-all duration-500"
      style={{ opacity: i < shown ? 1 : 0, transform: i < shown ? 'translateY(0)' : 'translateY(10px)' }}
    >
      {child}
    </div>
  ));
};

/* ---------- the living-arrangement curation (chapter 1 drives chapters 1–2) ---------- */

type ArrangementKey = 'aged_care' | 'home_care' | 'village' | 'granny_flat' | 'downsize';

export const EXPERIENCE_ARRANGEMENTS: { key: ArrangementKey; label: string }[] = [
  { key: 'aged_care', label: 'Aged care' },
  { key: 'home_care', label: 'Support at home' },
  { key: 'village', label: 'Retirement village' },
  { key: 'granny_flat', label: 'Granny flat' },
  { key: 'downsize', label: 'Downsizing' },
];

/**
 * Everything the tour curates per arrangement (illustrative figures): the mock fact-find, the
 * results tiles, the Suggestion Engine race, the Manual-control scenarios + insights, and AWise's
 * example question. Owner rule (10 Jul): the tour must showcase every module — e.g. Support at
 * Home is about SELECTING SERVICES within the funding budget, not just contribution modelling.
 */
interface StoryItem {
  label: string;
  value: string;
}

interface ResultMetric {
  label: string;
  target: number;
  tone: string;
}

interface StrategyOption {
  title: string;
  target: number;
}

interface StrategiesInfo {
  searched: number;
  metric: string;
  top: StrategyOption;
  alt: StrategyOption;
  punchline: [string, string, string];
}

interface ManualScenario {
  label: string;
  values: number[];
  insight: string;
}

interface ManualInfo {
  metrics: string[];
  a: ManualScenario;
  b: ManualScenario;
}

interface AwiseInfo {
  q: string;
  a: React.ReactNode;
}

interface CuratedArrangement {
  tag: string;
  story: StoryItem[];
  banner: string;
  results: ResultMetric[];
  strategies: StrategiesInfo;
  manual: ManualInfo;
  awise: AwiseInfo;
}

const CURATED: Record<ArrangementKey, CuratedArrangement> = {
  aged_care: {
    tag: 'Residential aged care',
    story: [
      { label: 'The family home', value: 'Owned · valued at $950,000' },
      { label: 'Savings & investments', value: '$380,000 · full Age Pension' },
    ],
    banner: 'We’ll model keeping, renting or selling the home — and who stays in it',
    results: [
      { label: 'Room price (RAD)', target: 550000, tone: 'text-[#1a6fd4]' },
      { label: 'Age Pension / yr', target: 29874, tone: 'text-emerald-600' },
      { label: 'Care fees / yr', target: 38915, tone: 'text-slate-800' },
    ],
    strategies: {
      searched: 327,
      metric: 'Estate after 10 years',
      top: { title: 'Keep the home & rent it, pay part-RAD', target: 1284000 },
      alt: { title: 'Sell the home, pay full RAD', target: 1121000 },
      punchline: ['A ', '$163,000', ' difference — found in seconds, explained in plain English.'],
    },
    manual: {
      metrics: ['Estate after 10 years', 'Age Pension / yr'],
      a: {
        label: 'Keep & rent the home',
        values: [1284000, 29874],
        insight:
          'Keeping the home holds onto an asset that keeps growing, and the rent helps cover the daily accommodation payment — though rent does count as income. For Margaret, this path leaves the larger estate.',
      },
      b: {
        label: 'Sell the home',
        values: [1121000, 22350],
        insight:
          'Selling pays the room in full — simpler, nothing owing daily — but the sale proceeds now count in the means test, so the pension falls. Simplicity has a price: a smaller estate over 10 years.',
      },
    },
    awise: {
      q: 'What happens to Mum’s pension if she keeps the house?',
      a: (
        <>
          Great question. While your mum is in care, her former home is treated specially — and if a
          <strong> protected person</strong> (like her partner) still lives there, it stays out of the
          aged care means test entirely. Your model on the next screen shows both cases…
        </>
      ),
    },
  },
  home_care: {
    tag: 'Support at Home',
    story: [
      { label: 'Staying at home', value: 'Support at Home · own home' },
      { label: 'Savings & investments', value: '$380,000 · full Age Pension' },
    ],
    banner: 'We’ll fit the right services inside your funding budget — and model your contributions',
    results: [
      { label: 'Govt funding / yr', target: 52000, tone: 'text-[#1a6fd4]' },
      { label: 'Your contribution / yr', target: 6400, tone: 'text-slate-800' },
      { label: 'Age Pension / yr', target: 29874, tone: 'text-emerald-600' },
    ],
    strategies: {
      searched: 214,
      metric: 'Out of pocket / yr',
      top: { title: 'Curated service mix — care hours, cleaning, transport — inside the budget', target: 4900 },
      alt: { title: 'Wish-list services, self-funded top-ups', target: 12300 },
      punchline: ['', '$7,400 a year', ' back in your pocket — the same care priorities, a smarter service mix.'],
    },
    manual: {
      metrics: ['Out of pocket / yr', 'Care hours / week'],
      a: {
        label: 'Essential services',
        values: [4900, 9],
        insight:
          'Your chosen services sit comfortably inside the government funding level, with room to spare. The panel prices every service line as you add it — so you always know what fits.',
      },
      b: {
        label: 'Add gardening + social support',
        values: [8200, 12],
        insight:
          'The extra services push past the funded budget, so the difference is self-funded. The panel shows exactly what each added hour costs you — trade-offs made visible, before you commit.',
      },
    },
    awise: {
      q: 'Can we add more help at home without blowing the budget?',
      a: (
        <>
          Often, yes. Your Support at Home budget covers a set amount each year — the tool shows what
          your chosen services cost against it, and where a <strong>different service mix</strong> (or a
          small self-funded top-up) gets you more of the help that matters most.
        </>
      ),
    },
  },
  village: {
    tag: 'Retirement village',
    story: [
      { label: 'The village', value: 'Entry price $650,000 · lease/licence' },
      { label: 'Savings & investments', value: '$380,000 · full Age Pension' },
    ],
    banner: 'We’ll model the entry price, exit fees, and your pension either side of the move',
    results: [
      { label: 'Entry contribution', target: 650000, tone: 'text-[#1a6fd4]' },
      { label: 'Age Pension / yr', target: 29874, tone: 'text-emerald-600' },
      { label: 'Exit fee (10 yrs)', target: 195000, tone: 'text-slate-800' },
    ],
    strategies: {
      searched: 186,
      metric: 'Estate after 10 years',
      top: { title: 'Lower entry price, standard exit fee', target: 1062000 },
      alt: { title: 'Premium entry price, capped exit fee', target: 1018000 },
      punchline: ['A ', '$44,000', ' difference — with the pension impact of each entry price shown before you sign.'],
    },
    manual: {
      metrics: ['Age Pension / yr', 'Estate after 10 years'],
      a: {
        label: 'Entry price $650,000',
        values: [29874, 1062000],
        insight:
          'At this entry price Centrelink treats you as a homeowner — the contribution itself is exempt from the assets test, and your pension holds steady.',
      },
      b: {
        label: 'Entry price $220,000',
        values: [33400, 1041000],
        insight:
          'Below the threshold you’re a non-homeowner: the contribution counts in the assets test, but higher limits apply and Rent Assistance can top up your pension. The maths cuts both ways — so we model it.',
      },
    },
    awise: {
      q: 'Do exit fees really matter that much?',
      a: (
        <>
          They can be the biggest cost of the whole move. A <strong>deferred management fee</strong>{' '}
          quietly grows every year you stay, and some contracts share your capital gain too — the tool
          projects what actually comes back to you, and your estate, when you leave.
        </>
      ),
    },
  },
  granny_flat: {
    tag: 'Granny flat',
    story: [
      { label: 'The arrangement', value: '$400,000 toward the kids’ home' },
      { label: 'Savings & investments', value: '$380,000 · full Age Pension' },
    ],
    banner: 'We’ll apply Centrelink’s reasonableness test to your contribution as you type',
    results: [
      { label: 'Your contribution', target: 400000, tone: 'text-[#1a6fd4]' },
      { label: 'Counted as a gift', target: 0, tone: 'text-emerald-600' },
      { label: 'Age Pension / yr', target: 31200, tone: 'text-emerald-600' },
    ],
    strategies: {
      searched: 158,
      metric: 'Age Pension / yr',
      top: { title: 'Contribute at the assessed value — nothing counts as a gift', target: 31200 },
      alt: { title: 'Contribute $100,000 above the assessed value', target: 27100 },
      punchline: ['Paying above the assessed value is a ', 'gift that counts for five years', ' — the engine finds the line before you cross it.'],
    },
    manual: {
      metrics: ['Counted as a gift', 'Age Pension / yr'],
      a: {
        label: 'Contribute $400,000',
        values: [0, 31200],
        insight:
          'Your contribution sits under Centrelink’s assessed value of the lifetime right — so none of it is treated as a gift, and your pension is untouched.',
      },
      b: {
        label: 'Contribute $520,000',
        values: [90000, 27100],
        insight:
          'The amount above the assessed value is deprivation — counted as if you still had it, for five years. The panel shows the exact overshoot so the family can set the contribution with confidence.',
      },
    },
    awise: {
      q: 'If I help the kids build a granny flat, will I lose my pension?',
      a: (
        <>
          Not if it’s structured well. Centrelink values your lifetime right to live there using the{' '}
          <strong>reasonableness test</strong> — pay up to that value and nothing is a gift. The tool
          calculates that value for your family’s numbers and flags the safe range.
        </>
      ),
    },
  },
  downsize: {
    tag: 'Downsizing',
    story: [
      { label: 'The move', value: 'Selling $950,000 · buying $700,000' },
      { label: 'Savings & investments', value: '$380,000 · full Age Pension' },
    ],
    banner: 'We’ll model the freed-up capital, super options, and the pension impact',
    results: [
      { label: 'Sale proceeds', target: 950000, tone: 'text-[#1a6fd4]' },
      { label: 'New home', target: 700000, tone: 'text-slate-800' },
      { label: 'Freed to invest', target: 250000, tone: 'text-emerald-600' },
    ],
    strategies: {
      searched: 243,
      metric: 'Estate after 10 years',
      top: { title: 'Downsizer contribution to super with the freed-up capital', target: 1395000 },
      alt: { title: 'Freed-up capital to the bank', target: 1344000 },
      punchline: ['A ', '$51,000', ' difference — with the pension effect of each path modelled before you list the house.'],
    },
    manual: {
      metrics: ['Estate after 10 years', 'Age Pension / yr'],
      a: {
        label: 'Downsizer super contribution',
        values: [1395000, 24600],
        insight:
          'Inside super the money still counts for the means test at pension age — but tax-effective earnings compound year after year. The panel shows the long-run difference, honestly.',
      },
      b: {
        label: 'Keep it in the bank',
        values: [1344000, 24600],
        insight:
          'In the bank the money is deemed to earn income and the interest is taxed. Notice the pension barely moves either way — it’s the estate line where this decision really shows up.',
      },
    },
    awise: {
      q: 'Will downsizing cost me my pension?',
      a: (
        <>
          It can, if the freed-up money isn’t planned for — dollars that were <strong>exempt</strong> in
          your home become <strong>assessable</strong> once they’re in the bank. The tool shows the
          pension impact of the move, and the strategies that soften it, before you commit.
        </>
      ),
    },
  },
};

/* ---------- chapter visuals (animated product mocks) ---------- */

interface VisualProps {
  replayKey: string | number;
  arrangement: ArrangementKey;
  onArrangement: (key: ArrangementKey) => void;
}

interface MockWindowProps {
  title: string;
  children: React.ReactNode;
}

const MockWindow = ({ title, children }: MockWindowProps) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-blue-200/40">
    <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
      <span className="ml-3 text-xs font-medium text-slate-500">{title}</span>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const FactFindVisual = ({ replayKey, arrangement, onArrangement }: VisualProps) => {
  const curated = CURATED[arrangement];
  return (
    <MockWindow title="AgedCareWise — Your story">
      <div className="mb-4">
        <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
          What are you exploring? <span className="normal-case text-slate-300">(try one)</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {EXPERIENCE_ARRANGEMENTS.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => onArrangement(a.key)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                a.key === arrangement
                  ? 'bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] text-white shadow'
                  : 'border border-slate-200 bg-white text-slate-600 hover:border-[#1a6fd4]/50 hover:text-[#1a6fd4]'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
      <Stagger replayKey={`${replayKey}-${arrangement}`}>
        <div className="mb-3 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <User size={18} className="text-[#1a6fd4]" />
          <div className="flex-1">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Your name</div>
            <div className="text-sm font-semibold text-slate-800">Margaret Wilson, 84</div>
          </div>
          <CheckCircle2 size={18} className="text-emerald-500" />
        </div>
        {curated.story.map(({ label, value }) => (
          <div key={label} className="mb-3 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <CheckCircle2 size={18} className="shrink-0 text-emerald-500" />
            <div className="flex-1">
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
              <div className="text-sm font-semibold text-slate-800">{value}</div>
            </div>
          </div>
        ))}
        <div className="rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-4 py-3 text-center text-sm font-semibold text-white shadow-md">
          {curated.banner}
        </div>
      </Stagger>
    </MockWindow>
  );
};

interface CountUpMetricProps {
  label: string;
  target: number;
  tone: string;
  replayKey: string | number;
  delay: number;
}

const CountUpMetric = ({ label, target, tone, replayKey, delay }: CountUpMetricProps) => {
  const value = useCountUp(target, 1600, replayKey, delay);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
      <div className={`text-lg font-bold tabular-nums ${tone}`}>{aud(value)}</div>
      <div className="mt-1 text-[11px] font-medium text-slate-500">{label}</div>
    </div>
  );
};

const ResultsVisual = ({ replayKey, arrangement }: VisualProps) => {
  const metrics = CURATED[arrangement].results;
  const bars = [62, 84, 47];
  const key = `${replayKey}-${arrangement}`;
  return (
    <MockWindow title="AgedCareWise — Your results">
      <div className="grid grid-cols-3 gap-3">
        {metrics.map((m, i) => (
          <CountUpMetric key={m.label} {...m} replayKey={key} delay={200 + i * 300} />
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 p-4">
        <div className="mb-3 text-xs font-semibold text-slate-500">Cashflow, pension & estate — projected over 10 years</div>
        <div className="flex h-24 items-end gap-4">
          {bars.map((h, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-1">
              <div
                className="w-full rounded-t-lg bg-gradient-to-t from-[#1a6fd4] to-[#17b8e0] transition-all duration-1000 ease-out"
                style={{ height: `${h}%`, transitionDelay: `${300 + i * 250}ms` }}
                key={`${key}-${i}`}
              />
              <span className="text-[10px] text-slate-400">{['Year 1', 'Year 5', 'Year 10'][i]}</span>
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
};

const StrategiesVisual = ({ replayKey, arrangement }: VisualProps) => {
  const s = CURATED[arrangement].strategies;
  const key = `${replayKey}-${arrangement}`;
  const topValue = useCountUp(s.top.target, 1500, key, 700);
  const altValue = useCountUp(s.alt.target, 1500, key, 900);
  const [pre, highlight, post] = s.punchline;
  return (
    <MockWindow title="AgedCareWise — Suggestion Engine">
      <div className="mb-3 flex items-center gap-2 text-xs font-medium text-slate-500">
        <TrendingUp size={14} className="text-[#1a6fd4]" />
        {s.searched} strategy combinations searched for Margaret…
      </div>
      <Stagger replayKey={key} step={600}>
        <div className="mb-3 rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="shrink-0 text-emerald-600" />
              <span className="text-sm font-bold text-slate-800">{s.top.title}</span>
            </div>
            <span className="shrink-0 rounded-full bg-emerald-500 px-2.5 py-0.5 text-[11px] font-bold text-white">Top strategy</span>
          </div>
          <div className="mt-2 flex items-end justify-between">
            <span className="text-xs text-slate-500">{s.metric}</span>
            <span className="text-lg font-bold tabular-nums text-emerald-700">{aud(topValue)}</span>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-slate-700">{s.alt.title}</span>
            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-500">Compared</span>
          </div>
          <div className="mt-2 flex items-end justify-between">
            <span className="text-xs text-slate-500">{s.metric}</span>
            <span className="text-lg font-bold tabular-nums text-slate-600">{aud(altValue)}</span>
          </div>
        </div>
        <div className="mt-3 text-center text-xs font-medium text-slate-500">
          {pre}<span className="font-bold text-emerald-600">{highlight}</span>{post}
        </div>
      </Stagger>
    </MockWindow>
  );
};

const ManualControlVisual = ({ replayKey, arrangement }: VisualProps) => {
  const m = CURATED[arrangement].manual;
  const [scenario, setScenario] = useState<'a' | 'b'>('a');
  useEffect(() => {
    setScenario('a');
    const t = setTimeout(() => setScenario('b'), 2800); // show the flip happening by itself
    return () => clearTimeout(t);
  }, [replayKey, arrangement]);
  const active = m[scenario];
  const key = `${replayKey}-${arrangement}-${scenario}`;
  const first = useCountUp(active.values[0], 1100, key);
  const second = useCountUp(active.values[1], 1100, key, 150);
  const shown = [first, second];
  return (
    <MockWindow title="AgedCareWise — Manual control">
      <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
        {(['a', 'b'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setScenario(k)}
            className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
              k === scenario ? 'bg-white text-[#1a6fd4] shadow' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {m[k].label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {m.metrics.map((label, i) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <div className={`text-lg font-bold tabular-nums ${i === 0 ? 'text-slate-800' : 'text-emerald-600'}`}>
              {label.includes('hours') ? shown[i] : aud(shown[i])}
            </div>
            <div className="mt-1 text-[11px] font-medium text-slate-500">{label}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white p-4">
        <div className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-700">
          <Lightbulb size={14} /> Insight
        </div>
        <p key={key} className="text-sm leading-relaxed text-slate-700">{active.insight}</p>
      </div>
      <div className="mt-3 text-center text-[11px] font-medium text-slate-400">
        Flip the setting — the numbers and the explanation update instantly.
      </div>
    </MockWindow>
  );
};

const SoaVisual = ({ replayKey, arrangement }: VisualProps) => (
  <MockWindow title="AgedCareWise — Statement of Advice">
    <div className="mx-auto max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-inner">
      <Stagger replayKey={`${replayKey}-${arrangement}`} step={380}>
        <div className="mb-3 flex items-center gap-2">
          <FileText size={18} className="shrink-0 text-[#1a6fd4]" />
          <span className="text-sm font-bold text-slate-800">
            Statement of Advice
            <span className="ml-1.5 font-medium text-slate-400">· {CURATED[arrangement].tag}</span>
          </span>
        </div>
        <div className="mb-2 h-2.5 w-3/4 rounded bg-slate-200" />
        <div className="mb-2 h-2.5 w-full rounded bg-slate-100" />
        <div className="mb-2 h-2.5 w-5/6 rounded bg-slate-100" />
        <div className="mb-4 h-2.5 w-2/3 rounded bg-slate-100" />
        <div className="mb-2 flex items-center gap-2 text-xs text-slate-600">
          <CheckCircle2 size={14} className="text-emerald-500" /> Your situation & goals
        </div>
        <div className="mb-2 flex items-center gap-2 text-xs text-slate-600">
          <CheckCircle2 size={14} className="text-emerald-500" /> The strategy, step by step
        </div>
        <div className="mb-2 flex items-center gap-2 text-xs text-slate-600">
          <CheckCircle2 size={14} className="text-emerald-500" /> Projections & assumptions, shown honestly
        </div>
        <div className="mt-4 rounded-lg bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] py-2 text-center text-xs font-semibold text-white">
          Your plan — ready to keep, share or action
        </div>
      </Stagger>
    </div>
  </MockWindow>
);

const AwiseVisual = ({ replayKey, arrangement }: VisualProps) => {
  const chat = CURATED[arrangement].awise;
  const [typing, setTyping] = useState(true);
  useEffect(() => {
    setTyping(true);
    const t = setTimeout(() => setTyping(false), 1600);
    return () => clearTimeout(t);
  }, [replayKey, arrangement]);
  return (
    <MockWindow title="AWise — your guide">
      <div className="space-y-3">
        <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-sm bg-[#1a6fd4] px-4 py-2.5 text-sm text-white">
          {chat.q}
        </div>
        <div className="flex items-start gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] text-white">
            <Bot size={16} />
          </div>
          {typing ? (
            <div className="rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '0ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '150ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          ) : (
            <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-2.5 text-sm leading-relaxed text-slate-700">
              {chat.a}
            </div>
          )}
        </div>
        <div className="text-center text-[11px] font-medium text-slate-400">
          AWise knows the rules on every screen — ask anything, any time.
        </div>
      </div>
    </MockWindow>
  );
};

const PartnerVisual = ({ replayKey }: VisualProps) => (
  <MockWindow title="AgedCareWise — Partner portal">
    <Stagger replayKey={replayKey} step={420}>
      <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div>
          <div className="text-sm font-bold text-slate-800">Taylor Accounting Pty Ltd</div>
          <div className="text-[11px] text-slate-500">Your firm’s secure workspace</div>
        </div>
        <Briefcase size={18} className="text-[#1a6fd4]" />
      </div>
      {[
        { name: 'Margaret Wilson', status: 'Plan complete · SOA ready', tone: 'bg-emerald-100 text-emerald-700' },
        { name: 'Frank & Elaine Rossi', status: 'Fact-find in progress', tone: 'bg-blue-100 text-blue-700' },
        { name: 'The Chen family', status: 'Invited · awaiting sign-in', tone: 'bg-amber-100 text-amber-700' },
      ].map(({ name, status, tone }) => (
        <div key={name} className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
          <span className="text-sm font-semibold text-slate-700">{name}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${tone}`}>{status}</span>
        </div>
      ))}
      <div className="rounded-xl border-2 border-dashed border-[#1a6fd4]/40 bg-blue-50/50 px-4 py-3 text-center text-sm font-semibold text-[#1a6fd4]">
        + Invite a client — they tell their story in their own secure login
      </div>
    </Stagger>
  </MockWindow>
);

/* ---------- chapters ---------- */

interface Chapter {
  key: string;
  nav: string;
  eyebrow: string;
  title: string;
  body: string;
  Visual: React.ComponentType<VisualProps>;
  interactive?: boolean;
}

const CHAPTERS: Chapter[] = [
  {
    key: 'story',
    nav: 'Your story',
    eyebrow: 'Step 1',
    title: 'Start with what you’re thinking',
    body:
      'Pick the living arrangement on your mind — staying home with support, a village, a granny flat, downsizing, or residential care — and AgedCareWise curates the whole journey to it. Then answer friendly, plain-English questions; everything saves automatically as you go. (Not sure which path? Explore them all — switching is free.)',
    Visual: FactFindVisual,
    interactive: true,
  },
  {
    key: 'results',
    nav: 'Real numbers',
    eyebrow: 'Step 2',
    title: 'See your real numbers, instantly',
    body:
      'The moment your story is in, AgedCareWise turns Australia’s means-testing rules into YOUR numbers for the path you chose — costs, entitlements, cashflow and estate, projected over the years ahead. Change the arrangement in step 1 and watch this screen re-curate.',
    Visual: ResultsVisual,
  },
  {
    key: 'strategies',
    nav: 'Strategies',
    eyebrow: 'Step 3',
    title: 'Watch strategies compete for you',
    body:
      'The Suggestion Engine searches hundreds of combinations for the path you chose and shows you which wins, by how much, and why. For aged care that’s keep-or-sell the home and lump-sum-or-daily-fee; for care at home it’s fitting the right services inside your funding budget; for a granny flat, finding the gifting line before you cross it. Change the arrangement in step 1 and this race re-runs.',
    Visual: StrategiesVisual,
  },
  {
    key: 'manual',
    nav: 'Manual control',
    eyebrow: 'Step 4',
    title: 'Or take the controls yourself',
    body:
      'Prefer your own hands on the wheel? Manual control lets you build and compare your own scenarios for whichever path you’re exploring. As you change a setting, the numbers move and an Insight panel explains the implications — just like a human adviser would, live and instantaneous.',
    Visual: ManualControlVisual,
  },
  {
    key: 'soa',
    nav: 'Your plan',
    eyebrow: 'Step 5',
    title: 'Walk away with a professional plan',
    body:
      'Your chosen strategy becomes a professional Statement of Advice — your situation, the recommendation, and honest projections — ready to keep, share with family, or act on.',
    Visual: SoaVisual,
  },
  {
    key: 'awise',
    nav: 'Meet AWise',
    eyebrow: 'Always with you',
    title: 'Never stuck, never alone',
    body:
      'AWise, your built-in AI guide, understands aged-care rules and the exact screen you’re on. Ask anything in plain English, whenever a question pops into your head.',
    Visual: AwiseVisual,
  },
  {
    key: 'partners',
    nav: 'Partners & providers',
    eyebrow: 'For professionals & providers',
    title: 'Guide every client, at scale',
    body:
      'Accountants, financial advisers, aged-care and home-care providers, and retirement-living operators run their clients through this same engine from a secure firm workspace. Invite clients by link and track every file — invited, in progress, plan ready. Advisers advise under their own AFSL; other partners simply provide access, with the advice provided by AgedCareWise.',
    Visual: PartnerVisual,
  },
];

/* ---------- page ---------- */

export interface ExperiencePageProps {
  onBack: () => void;
  onGetStarted?: () => void;
  onBecomePartner?: () => void;
}

const ExperiencePage = ({ onBack, onGetStarted, onBecomePartner }: ExperiencePageProps) => {
  const [chapter, setChapter] = useState(0);
  const [replayKey, setReplayKey] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [arrangement, setArrangement] = useState<ArrangementKey>('aged_care');
  const active = CHAPTERS[chapter];
  const { Visual } = active;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Gentle auto-advance so the page "plays itself" like a tour; any manual action takes over.
  useEffect(() => {
    if (!autoPlay) return undefined;
    timerRef.current = setTimeout(() => {
      setChapter((c) => (c + 1 < CHAPTERS.length ? c + 1 : c));
      if (chapter + 1 >= CHAPTERS.length - 1) setAutoPlay(false);
    }, 7000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [chapter, autoPlay]);

  const goTo = (i: number) => {
    setAutoPlay(false);
    setChapter(i);
    setReplayKey((k) => k + 1);
  };

  const pickArrangement = (key: ArrangementKey) => {
    setAutoPlay(false); // the visitor is playing — don't yank the tour forward
    setArrangement(key);
  };

  const progress = useMemo(() => ((chapter + 1) / CHAPTERS.length) * 100, [chapter]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div className="text-lg font-bold">
            <span className="text-slate-900">AgedCare</span><span className="text-[#17b8e0]">Wise</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] text-white">
        <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
        <div className="relative mx-auto max-w-5xl px-5 py-12 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
            <Play className="h-8 w-8" />
          </div>
          <h1 className="text-3xl font-bold sm:text-4xl">See it before you start</h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-white/90 sm:text-lg">
            A two-minute tour of exactly what happens inside AgedCareWise — from your first answer to a
            plan you can act on.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 py-10">
        {/* Chapter navigation */}
        <div className="mb-2 flex flex-wrap items-center justify-center gap-2">
          {CHAPTERS.map((c, i) => (
            <button
              key={c.key}
              type="button"
              onClick={() => goTo(i)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition sm:text-sm ${
                i === chapter
                  ? 'bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-[#1a6fd4]/50 hover:text-[#1a6fd4]'
              }`}
            >
              {c.nav}
            </button>
          ))}
        </div>
        {/* Progress bar */}
        <div className="mx-auto mb-8 h-1 max-w-md overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Active chapter */}
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <div className="text-sm font-semibold uppercase tracking-wider text-[#1a6fd4]">{active.eyebrow}</div>
            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">{active.title}</h2>
            <p className="mt-3 text-base leading-relaxed text-gray-600">{active.body}</p>
            <div className="mt-6 flex items-center gap-3">
              {chapter < CHAPTERS.length - 1 ? (
                <button
                  type="button"
                  onClick={() => goTo(chapter + 1)}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:shadow-lg"
                >
                  Next <ArrowRight size={16} />
                </button>
              ) : (
                onGetStarted && (
                  <button
                    type="button"
                    onClick={onGetStarted}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:shadow-lg"
                  >
                    <Sparkles size={16} /> Start your plan
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => setReplayKey((k) => k + 1)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:border-[#1a6fd4]/50 hover:text-[#1a6fd4]"
              >
                <RotateCcw size={14} /> Replay
              </button>
            </div>
            {active.key === 'manual' && (
              <div className="mt-4 flex items-center gap-2 text-xs font-medium text-slate-400">
                <SlidersHorizontal size={14} /> Automation when you want it, control when you don’t.
              </div>
            )}
          </div>
          <div className="order-1 lg:order-2">
            <Visual
              replayKey={`${chapter}-${replayKey}`}
              arrangement={arrangement}
              onArrangement={pickArrangement}
            />
          </div>
        </div>

        {/* Closing CTA */}
        <div className="mt-14 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-7 text-center text-white sm:p-10">
          <h2 className="text-xl font-bold sm:text-2xl">Ready when you are</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base">
            Everything you just saw runs on your real numbers the moment you begin — and your first
            answers take about ten minutes.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            {onGetStarted && (
              <button
                type="button"
                onClick={onGetStarted}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-7 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl"
              >
                <Sparkles size={16} /> Get Started
              </button>
            )}
            {onBecomePartner && (
              <button
                type="button"
                onClick={onBecomePartner}
                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                For partners & providers <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperiencePage;
